import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react"
import { useTranslation } from "@/hooks/use-translation"
import {
  FOV_APEX_DOT,
  FOV_APEX_X,
  FOV_APEX_Y,
  FOV_FAN_STROKE,
  FOV_FOCUS_STROKE,
  FOV_FRAME_HEIGHT_MM,
  FOV_LABEL_INSET,
  FOV_LABELED_FOCALS,
  FOV_MAJOR_TICK,
  FOV_MINOR_FOCALS,
  FOV_MINOR_TICK,
  FOV_RADIUS,
  FOV_SELECT_TOLERANCE,
  FOV_VALUE_STROKE,
  FOV_VALUE_TEXT_Y,
  clampFov,
  focalInRange,
  fovControlFrame,
  fovFromVerticalDrag,
  fovOfFocal,
  pointOnFan,
  sectorPath,
} from "@/lib/location-scene-3d/fov-control-math"

const COLOR = {
  face: "var(--surface-raised)",
  edge: "var(--clay-6)",
  value: "var(--fov-value)",
  valueActive: "var(--fov-value-active)",
  valueEdge: "var(--fov-value-edge)",
  valueText: "var(--clay-9)",
  number: "var(--clay-7)",
  numberHover: "var(--fov-number-hover)",
  numberSelected: "var(--fov-number-selected)",
  focus: "var(--fov-focus)",
} as const

function scaleNumberColor(selected: boolean, hot: boolean) {
  if (selected) return COLOR.numberSelected
  if (hot) return COLOR.numberHover
  return COLOR.number
}

type FovControlProps = {
  value: number
  min: number
  max: number
  labeledFocals?: readonly number[]
  minorFocals?: readonly number[]
  frameHeight?: number
  onInput: (value: number) => void
  onChange: (value: number) => void
}

type TickLine = {
  key: string
  x1: number
  y1: number
  x2: number
  y2: number
  width: number
}

function tickLines(mm: number, length: number, width: number, frameHeight: number): TickLine[] {
  const half = fovOfFocal(mm, frameHeight) / 2
  return [-half, half].map((angle) => {
    const [x1, y1] = pointOnFan(FOV_RADIUS, angle)
    const [x2, y2] = pointOnFan(FOV_RADIUS - length, angle)
    return { key: `${mm}:${angle < 0 ? "l" : "r"}`, x1, y1, x2, y2, width }
  })
}

export function FovControl({
  value,
  min,
  max,
  labeledFocals = FOV_LABELED_FOCALS,
  minorFocals = FOV_MINOR_FOCALS,
  frameHeight = FOV_FRAME_HEIGHT_MM,
  onInput,
  onChange,
}: FovControlProps) {
  const { t } = useTranslation("workspace")
  const rootRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const valueRef = useRef(clampFov(value, min, max, min))
  const draggingRef = useRef(false)
  const dragStartRef = useRef<number | null>(null)
  const dragOriginYRef = useRef(0)
  const onInputRef = useRef(onInput)
  const onChangeRef = useRef(onChange)
  const commitRef = useRef<(raw: number, final: boolean) => boolean>(() => false)
  const [dragging, setDragging] = useState(false)
  const [svgHover, setSvgHover] = useState(false)
  const [hoveredMm, setHoveredMm] = useState<number | null>(null)
  const [focusVisible, setFocusVisible] = useState(false)

  const clamped = clampFov(value, min, max, min)
  valueRef.current = clamped
  onInputRef.current = onInput
  onChangeRef.current = onChange

  const commit = (raw: number, final: boolean) => {
    const next = clampFov(raw, min, max, valueRef.current)
    if (next === valueRef.current) return false
    valueRef.current = next
    onInputRef.current(next)
    if (final) onChangeRef.current(next)
    return true
  }
  commitRef.current = commit

  const frame = useMemo(() => fovControlFrame(max), [max])
  const fanPath = useMemo(() => sectorPath(FOV_RADIUS, max / 2), [max])
  const valuePath = sectorPath(FOV_RADIUS, clamped / 2)

  const ticks = useMemo(() => {
    const inRange = (mm: number) => focalInRange(mm, min, max, frameHeight)
    const minor = minorFocals
      .filter(inRange)
      .flatMap((mm) => tickLines(mm, FOV_MINOR_TICK.length, FOV_MINOR_TICK.width, frameHeight))
    const major = labeledFocals
      .filter(inRange)
      .flatMap((mm) => tickLines(mm, FOV_MAJOR_TICK.length, FOV_MAJOR_TICK.width, frameHeight))
    return [...minor, ...major]
  }, [frameHeight, labeledFocals, max, min, minorFocals])

  const numbers = useMemo(() => {
    return labeledFocals
      .filter((mm) => focalInRange(mm, min, max, frameHeight))
      .map((mm) => {
        const [x, y] = pointOnFan(FOV_RADIUS - FOV_LABEL_INSET, fovOfFocal(mm, frameHeight) / 2)
        return { mm, x, y }
      })
  }, [frameHeight, labeledFocals, max, min])

  useEffect(() => {
    const svg = svgRef.current
    const root = rootRef.current
    if (!svg || !root) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      event.stopPropagation()
      if (event.deltaY === 0) return
      commitRef.current(valueRef.current + (event.deltaY < 0 ? 1 : -1), true)
    }
    const preventSelect = (event: Event) => {
      event.preventDefault()
    }
    svg.addEventListener("wheel", onWheel, { passive: false })
    root.addEventListener("selectstart", preventSelect)
    return () => {
      svg.removeEventListener("wheel", onWheel)
      root.removeEventListener("selectstart", preventSelect)
    }
  }, [])

  const readDrag = (clientY: number) => {
    const start = dragStartRef.current
    if (start == null) return valueRef.current
    const svg = svgRef.current
    if (!svg) return valueRef.current
    const rect = svg.getBoundingClientRect()
    if (rect.height <= 0) return valueRef.current
    const deltaY = ((clientY - dragOriginYRef.current) * frame.height) / rect.height
    return fovFromVerticalDrag(start, deltaY, frame.height, min, max)
  }

  const endDrag = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    const svg = svgRef.current
    if (svg?.hasPointerCapture(event.pointerId)) svg.releasePointerCapture(event.pointerId)
    if (dragStartRef.current != null && valueRef.current !== dragStartRef.current) {
      onChangeRef.current(valueRef.current)
    }
    dragStartRef.current = null
  }

  const active = dragging || svgHover
  const label = t("locationsPage.scene3d.fovControl.label")

  return (
    <div
      ref={rootRef}
      className="block leading-none select-none [-webkit-touch-callout:none]"
      onPointerDown={(event) => event.stopPropagation()}
      onPointerMove={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onContextMenu={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      <svg
        ref={svgRef}
        width={frame.width}
        height={frame.height}
        viewBox={`${frame.x} ${frame.y} ${frame.width} ${frame.height}`}
        tabIndex={0}
        role="slider"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={clamped}
        aria-valuetext={t("locationsPage.scene3d.fovControl.degrees", { count: clamped })}
        className="block shrink-0 touch-none overflow-visible outline-none select-none"
        style={{ cursor: "ns-resize", fontFamily: "inherit" }}
        onPointerEnter={() => setSvgHover(true)}
        onPointerLeave={() => setSvgHover(false)}
        onFocus={() => setFocusVisible(svgRef.current?.matches(":focus-visible") ?? false)}
        onBlur={() => setFocusVisible(false)}
        onPointerDown={(event) => {
          if (event.pointerType === "mouse" && event.button !== 0) return
          event.preventDefault()
          const svg = svgRef.current
          if (!svg) return
          svg.focus({ preventScroll: true })
          setFocusVisible(false)
          svg.setPointerCapture(event.pointerId)
          draggingRef.current = true
          dragStartRef.current = valueRef.current
          dragOriginYRef.current = event.clientY
          setDragging(true)
        }}
        onPointerMove={(event) => {
          if (!draggingRef.current) return
          commit(readDrag(event.clientY), false)
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 5 : 1
          let next: number | null = null
          if (event.key === "ArrowRight" || event.key === "ArrowUp") next = valueRef.current + step
          else if (event.key === "ArrowLeft" || event.key === "ArrowDown") next = valueRef.current - step
          else if (event.key === "Home") next = min
          else if (event.key === "End") next = max
          if (next == null) return
          event.preventDefault()
          event.stopPropagation()
          setFocusVisible(true)
          commit(next, true)
        }}
      >
        <path
          d={fanPath}
          fill={COLOR.face}
          stroke={focusVisible ? COLOR.focus : COLOR.edge}
          strokeWidth={focusVisible ? FOV_FOCUS_STROKE : FOV_FAN_STROKE}
          strokeLinejoin="round"
        />
        <path
          d={valuePath}
          fill={active ? COLOR.valueActive : COLOR.value}
          stroke={COLOR.valueEdge}
          strokeWidth={FOV_VALUE_STROKE}
          strokeLinejoin="round"
          style={{ transition: "fill .1s" }}
        />
        {ticks.map((tick) => (
          <line
            key={tick.key}
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke={COLOR.edge}
            strokeWidth={tick.width}
            strokeLinecap="round"
          />
        ))}
        <circle cx={FOV_APEX_X} cy={FOV_APEX_Y} r={FOV_APEX_DOT} fill={COLOR.edge} />
        <text
          x={FOV_APEX_X}
          y={FOV_VALUE_TEXT_Y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={16}
          fill={COLOR.valueText}
          style={{ pointerEvents: "none" }}
        >
          {clamped}°
        </text>
        {numbers.map((number) => {
          const selected = Math.abs(fovOfFocal(number.mm, frameHeight) - clamped) < FOV_SELECT_TOLERANCE
          const hot = hoveredMm === number.mm
          const numberFill = scaleNumberColor(selected, hot)
          const focalLabel = t("locationsPage.scene3d.fovControl.focalMm", { mm: number.mm })
          return (
            <g
              key={number.mm}
              role="button"
              aria-label={focalLabel}
              style={{ cursor: "pointer" }}
              onPointerDown={(event) => {
                event.stopPropagation()
                event.preventDefault()
                svgRef.current?.focus({ preventScroll: true })
                setFocusVisible(false)
              }}
              onPointerUp={(event) => {
                if (event.pointerType === "mouse" && event.button !== 0) return
                event.stopPropagation()
                event.preventDefault()
                commit(fovOfFocal(number.mm, frameHeight), true)
              }}
              onPointerEnter={() => setHoveredMm(number.mm)}
              onPointerLeave={() => setHoveredMm((current) => (current === number.mm ? null : current))}
            >
              <title>{focalLabel}</title>
              <rect x={number.x - 9} y={number.y - 7} width={18} height={14} fill="transparent" />
              <text
                x={number.x}
                y={number.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={11}
                fontWeight={selected ? 500 : 400}
                fill={numberFill}
                style={{ pointerEvents: "none", transition: "fill .1s" }}
              >
                {number.mm}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
